import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertService, MessageSeverity } from '../../services/alert.service';
import { SfDataService } from '../../services/sf-data.service';
import { Utilities } from '../../services/utilities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare var $: any;

@Component({
  selector: 'app-email-composition',
  templateUrl: './email-composition.component.html',
  styleUrls: ['./email-composition.component.scss'],
  standalone: false
})
export class EmailCompositionComponent implements OnInit, OnDestroy {
  @ViewChild('bodyTextArea') bodyTextArea: ElementRef;

  emailForm: FormGroup;
  isSending = false;
  showPreview = false;
  availableUsers: any[] = [];
  selectedUser: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private alertService: AlertService,
    private sfDataService: SfDataService
  ) {
    this.emailForm = this.fb.group({
      mailTo: ['', [Validators.required, Validators.email]],
      mailCC: [''],
      mailBCC: [''],
      mailSubject: ['', [Validators.required, Validators.minLength(3)]],
      mailBody: ['', [Validators.required, Validators.minLength(10)]]
    });
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    this.sfDataService.getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (users: any) => {
          ////console.log('Raw API Response:', users); // Debug log
          
          // Format users for ng-select display
          this.availableUsers = (users || []).map((user: any) => {  
            const formattedUser = {
              id: user.id,
              email: user.email,
              userName: user.userName,
              fullName: user.fullName || user.userName,
              roles: user.roles || []
            };
            ////console.log('Formatted User:', formattedUser); // Debug log
            return formattedUser;
          });
          
          ////console.log('Available Users:', this.availableUsers); // Debug log
        },
        (error) => {
          console.error('Error loading users:', error); // Debug log
          this.alertService.showMessage(
            'Warning',
            'Could not load user list',
            MessageSeverity.warn
          );
        }
      );
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.emailForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onUserSelected(user: any): void {
    this.selectedUser = user;
  }

  addToRecipients(): void {
    if (!this.selectedUser) {
      this.alertService.showMessage(
        'Warning',
        'Please select a user first',
        MessageSeverity.warn
      );
      return;
    }

    const currentTo = this.emailForm.get('mailTo')?.value || '';
    // Extract email from the selected user (selectedUser is already the email from bindValue)
    const userEmail = typeof this.selectedUser === 'string' ? this.selectedUser : this.selectedUser.email;
    const newTo = currentTo ? `${currentTo}, ${userEmail}` : userEmail;
    this.emailForm.patchValue({ mailTo: newTo });
    this.selectedUser = null;
  }

  insertTag(tag: string): void {
    const textarea = this.bodyTextArea?.nativeElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const openTag = `<${tag}>`;
    const closeTag = `</${tag}>`;
    const newText = selectedText ? `${openTag}${selectedText}${closeTag}` : `${openTag}${closeTag}`;

    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    const updatedValue = beforeText + newText + afterText;

    this.emailForm.patchValue({ mailBody: updatedValue });

    // Move cursor inside the tags
    setTimeout(() => {
      const newCursorPos = start + openTag.length + selectedText.length;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  }

  insertLineBreak(): void {
    const textarea = this.bodyTextArea?.nativeElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);
    const updatedValue = beforeText + '<br/>' + afterText;

    this.emailForm.patchValue({ mailBody: updatedValue });

    setTimeout(() => {
      const newCursorPos = start + 5;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      textarea.focus();
    }, 0);
  }

  sendEmail(): void {
    if (!this.emailForm.valid) {
      this.alertService.showMessage(
        'Validation Error',
        'Please fill all required fields correctly.',
        MessageSeverity.error
      );
      return;
    }

    this.isSending = true;
    const emailData = this.emailForm.value;

    this.sfDataService.sendComposedEmail(emailData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.isSending = false;

          if (response && response.isSuccess) {
            this.alertService.showMessage(
              'Success',
              'Email sent successfully!',
              MessageSeverity.success
            );

            // Reset form and close modal
            this.resetForm();
            ($('#emailCompositionModal') as any).modal('hide');

            // Refresh email logs table if available
            this.refreshEmailLogs();
          } else {
            this.alertService.showMessage(
              'Error',
              response?.description || 'Failed to send email.',
              MessageSeverity.error
            );
          }
        },
        (error: any) => {
          this.isSending = false;
          this.alertService.showStickyMessage(
            'Error',
            `Failed to send email: "${Utilities.getHttpResponseMessages(error)}"`,
            MessageSeverity.error,
            error
          );
        }
      );
  }

  resetForm(): void {
    this.emailForm.reset();
    this.showPreview = false;
    this.selectedUser = null;
  }

  private refreshEmailLogs(): void {
    try {
      const table = ($('#tbldata') as any).DataTable();
      if (table) {
        table.ajax.reload();
      }
    } catch (e) {
      // Table might not be initialized yet
    }
  }
}
